import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import { EASE } from "@/lib/motion";

const schema = z.object({
  name: z.string().min(2, "Enter your name."),
  email: z.string().email("Enter a valid work email."),
  company: z.string().min(2, "Enter your company."),
  region: z.string().min(1, "Select a country or region."),
  note: z.string().optional(),
});

const REGIONS = [
  "South Africa",
  "Rest of Africa",
  "United Kingdom",
  "Europe",
  "Middle East",
  "Asia-Pacific",
  "Americas",
  "Other",
];

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
    {error ? <span className="mt-1.5 block text-xs text-red-400">{error}</span> : null}
  </label>
);

export default function DemoForm({ source = "modal" }) {
  const [status, setStatus] = useState("idle");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values) => {
    setStatus("submitting");
    const payload = {
      name: values.name,
      email: values.email,
      company: values.company,
      region: values.region,
      note: values.note || "",
      website: "",
    };
    try {
      const res = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Request failed");
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="flex flex-col items-center py-10 text-center"
        data-testid="demo-form-success"
      >
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
          className="flex h-16 w-16 items-center justify-center rounded-full border border-teal/40 bg-teal/10"
        >
          <CheckCircle2 className="h-8 w-8 text-teal" />
        </motion.span>
        <h3 className="mt-6 font-display text-2xl font-semibold text-slate-50">Demo request received</h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
          Thank you. A RORAQ specialist will review your payroll setup and reach out to arrange a tailored
          walkthrough of the platform.
        </p>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
          Next step - we confirm a time by email
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid={`demo-form-${source}`} noValidate>
      <Field label="Name *" error={errors.name?.message}>
        <input {...register("name")} data-testid="demo-form-name" className="input-field" placeholder="Jane" autoComplete="name" />
      </Field>
      <Field label="Work email *" error={errors.email?.message}>
        <input {...register("email")} data-testid="demo-form-email" type="email" className="input-field" placeholder="jane@company.com" autoComplete="email" />
      </Field>
      <Field label="Company *" error={errors.company?.message}>
        <input {...register("company")} data-testid="demo-form-company" className="input-field" placeholder="Company (Pty) Ltd" autoComplete="organization" />
      </Field>
      <Field label="Country / region *" error={errors.region?.message}>
        <select {...register("region")} data-testid="demo-form-region" className="input-field appearance-none pr-9" defaultValue="">
          <option value="" disabled>
            Select region
          </option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="What would you like RORAQ to help with? (optional)" error={errors.note?.message}>
          <textarea
            {...register("note")}
            data-testid="demo-form-note"
            rows={3}
            className="input-field resize-none"
            placeholder="Month-end packs across entities, workforce reporting, automation..."
          />
        </Field>
      </div>
      {status === "error" && (
        <p className="text-sm text-red-400 sm:col-span-2" data-testid="demo-form-error">
          Something went wrong submitting your request. Please try again, or email hello@roraq.com.
        </p>
      )}
      <div className="sm:col-span-2">
        <button type="submit" disabled={status === "submitting"} data-testid="demo-form-submit" className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60">
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending request
            </>
          ) : (
            "Request a demo"
          )}
        </button>
        <p className="mt-3 text-center text-xs text-slate-600">
          Your details are used only to arrange your demo. We do not sell data.
        </p>
      </div>
    </form>
  );
}
